import { Trash2 } from "lucide-react";
import { useState } from "react";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  Button,
  Card,
  PageSection,
  StatusMessage,
} from "@ceedero/ui";

import { useDeleteAccount } from "@/account/hooks/use-account";
import { resolveCredentialChangeErrorKey } from "@/account/utils/account-refusal";
import { validateCurrentPassword } from "@/account/utils/credential-validation";
import { FormField } from "@/components/form-field";
import { useTranslation } from "@/localization/hooks/use-translation";

import type { TranslationKey } from "@/localization/hooks/use-translation";

/**
 * Deleting the account and everything stored in it.
 *
 * What goes and what stays is said on the page, before the dialog, because the
 * two halves are easy to guess wrong in both directions: the cloud library goes
 * for good, while the projects on the user's own computers and the product key
 * they bought are untouched. Somebody who believed the key went too would not
 * delete an account they no longer want; somebody who believed their synced
 * projects stayed would lose them.
 *
 * The password is asked for in the dialog rather than trusted from the session,
 * which is only evidence that somebody signed in once. The dialog stays open
 * while the request runs and on a refusal, so a mistyped password is corrected
 * where it was typed.
 */
export const DeleteAccountSection = () => {
  const { t } = useTranslation();
  const deleteAccount = useDeleteAccount();
  const [isConfirming, setIsConfirming] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<TranslationKey | undefined>(undefined);

  const refusalKey = resolveCredentialChangeErrorKey(deleteAccount.error);

  const close = () => {
    setIsConfirming(false);
    setPassword("");
    setPasswordError(undefined);
    deleteAccount.reset();
  };

  const submit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const error = validateCurrentPassword(password);
    setPasswordError(error);
    if (error !== undefined) {
      return;
    }
    deleteAccount.mutate(password);
  };

  return (
    <PageSection title={t("account.delete.title")} description={t("account.delete.description")}>
      <Card className="flex flex-col gap-4 p-5">
        <p className="text-sm text-muted-foreground">{t("account.delete.details")}</p>
        <div className="flex justify-end">
          <Button type="button" variant="destructive" onClick={() => setIsConfirming(true)}>
            <Trash2 />
            {t("account.delete.open")}
          </Button>
        </div>
      </Card>

      <AlertDialog
        open={isConfirming}
        onOpenChange={(open) => {
          if (!open && !deleteAccount.isPending) {
            close();
          }
        }}
      >
        <AlertDialogContent>
          <form onSubmit={submit} noValidate className="contents">
            <AlertDialogTitle>{t("account.delete.confirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("account.delete.confirmDescription")}
            </AlertDialogDescription>
            <div className="mt-4 flex flex-col gap-3">
              <FormField
                label={t("account.username.currentPassword")}
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setPasswordError(undefined);
                  deleteAccount.reset();
                }}
                autoComplete="current-password"
                errorMessage={passwordError === undefined ? undefined : t(passwordError)}
              />
              {refusalKey !== undefined && (
                <StatusMessage tone="error">{t(refusalKey)}</StatusMessage>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel asChild>
                <Button type="button" variant="ghost" disabled={deleteAccount.isPending}>
                  {t("account.cancel")}
                </Button>
              </AlertDialogCancel>
              <Button type="submit" variant="destructive" isLoading={deleteAccount.isPending}>
                {t("account.delete.confirm")}
              </Button>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </PageSection>
  );
};
